// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { type AvailableShipmentFee } from '@alto/scriptdash/alto/orders/types/v1/available_shipment_fee';
import { type ShipmentFeeType } from '@alto/scriptdash/alto/orders/types/v1/shipment_fee_type';
import { add, addDays, format, formatISO, isAfter, isBefore, isSameDay, isToday, isTomorrow, parse } from 'date-fns';
import { DATE_FORMATS } from '~shared/helpers/date';
import { getFormattedPrice } from '~shared/helpers/pricingBreakdown';
import { type RadioValue } from './types';
import { type CheckoutDateItem } from './useSelectDateCard';

export const getSelectDateTitle = ({
  orderDate,
  dateToFeeHash,
  radioValue,
  earliestAvailableRadioVisible,
  earliestAvailableDate,
}: {
  orderDate: string | undefined | null;
  dateToFeeHash: Record<string, ShipmentFeeType | null>;
  radioValue?: string;
  earliestAvailableRadioVisible?: boolean;
  earliestAvailableDate?: string;
}) => {
  // return 'Select other date' if no date selected
  if (!orderDate) {
    return 'Select other date';
  }
  // return 'Select other date' if other options selected
  const feeForDate = dateToFeeHash[orderDate];
  if (feeForDate === 'same_day_delivery' || feeForDate === 'next_day_delivery') {
    return 'Select other date';
  }

  if (
    radioValue === 'same-day-order-bundling' ||
    radioValue === 'order-bundling' ||
    radioValue === 'earliest-available-date' ||
    radioValue === 'no-selection'
  ) {
    return 'Select other date';
  }

  if (earliestAvailableRadioVisible && earliestAvailableDate === orderDate) {
    return 'Select other date';
  }

  // return the formatted selected date to be the title
  return format(parse(orderDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0), DATE_FORMATS.WEEKDAY_MONTH_ORDINAL_DATE);
};

export const getPreSelectRadioValueForOrder = ({
  hasSchedulableDeliveryFeesInCart,
  orderDate,
  dateToFeeHash,
  hasFutureOrderBundlingOption,
  earliestAvailableRadioVisible,
  earliestAvailableDate,
  radioValue,
}: {
  hasSchedulableDeliveryFeesInCart: boolean;
  orderDate: string | undefined | null;
  dateToFeeHash: Record<string, ShipmentFeeType | null>;
  hasFutureOrderBundlingOption?: boolean;
  earliestAvailableRadioVisible?: boolean;
  earliestAvailableDate?: string;
  radioValue?: string;
}) => {
  const feeForDate = dateToFeeHash[orderDate || ''];
  // if an order bundling option is already selected, persist the selection
  if (radioValue === 'same-day-order-bundling') return 'same-day-order-bundling';
  if (radioValue === 'order-bundling') return 'order-bundling';

  // if the order date matches a same-day or next-day option, select it
  if (feeForDate === 'same_day_delivery') return 'same-day-delivery';
  if (feeForDate === 'next_day_delivery') return 'next-day-delivery';

  // always let the patient select which option when delivery fees are offered
  if (!orderDate && hasSchedulableDeliveryFeesInCart) return 'no-selection';

  // if there's a separate radio button for the earliest available date and the order date matches it, select it
  if (earliestAvailableRadioVisible && earliestAvailableDate === orderDate) return 'earliest-available-date';

  // if there's a bundled option and any-day-delivery is not already selected, default to order bundling
  if (hasFutureOrderBundlingOption && radioValue !== 'any-day-delivery') return 'order-bundling';

  // If the selected date exists and is not one of same or next day delivery, it is assumed to be standard delivery.
  if (orderDate) return 'any-day-delivery';
  return 'no-selection';
};

type GetCalendarPickerMinDateProps = {
  feeToDateHash: Record<ShipmentFeeType, string | null>;
  earliestAvailableDate: string;
};

// We exclude the same-day fee and next-day fee options from the standard calendar picker.
// As such, the min date should be after them, if present.
export const getCalendarPickerMinDate = ({ feeToDateHash, earliestAvailableDate }: GetCalendarPickerMinDateProps) => {
  const availableDate = parse(earliestAvailableDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0);
  const sameDayFeeDate = feeToDateHash.same_day_delivery;
  const nextDayFeeDate = feeToDateHash.next_day_delivery;

  //if next day delivery is schedulable, return the date after
  if (nextDayFeeDate) {
    const nextDayDate = parse(nextDayFeeDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0);
    if (!isBefore(nextDayDate, availableDate)) {
      return formatISO(add(nextDayDate, { days: 1 }), { representation: 'date' });
    }
  }

  //if same day delivery is schedulable, return tomorrow
  if (sameDayFeeDate) {
    const sameDayDate = parse(sameDayFeeDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0);
    if (isSameDay(sameDayDate, availableDate)) {
      //return tomorrow
      return formatISO(add(sameDayDate, { days: 1 }), { representation: 'date' });
    }
  }

  // otherwise, return the earliest available date
  return earliestAvailableDate;
};

type GetInitialSelectedDateProps = {
  minDate: string;
  orderDate: string | undefined | null;
  dateToFeeHash: Record<string, ShipmentFeeType | null>;
};

export const getInitialSelectedDate = ({ minDate, orderDate, dateToFeeHash }: GetInitialSelectedDateProps) => {
  const appliedFee = dateToFeeHash[orderDate || ''];
  const sameDayFeeApplied = appliedFee === 'same_day_delivery';
  const nextDayFeeApplied = appliedFee === 'next_day_delivery';

  // return order date if the order date was not selected from the other options
  if (orderDate && !sameDayFeeApplied && !nextDayFeeApplied) {
    return orderDate;
  }

  // otherwise, return min schedulable date
  return minDate;
};

export const shouldShowFeeDeliveryOption = ({
  feeToDateHash,
  earliestAvailableDate,
  fee_type,
  orderBundlingDate,
}: {
  feeToDateHash: Record<ShipmentFeeType, string | null>;
  earliestAvailableDate: string;
  fee_type: ShipmentFeeType;
  orderBundlingDate?: string;
}) => {
  const dateFromFee = feeToDateHash[fee_type];
  const availableDate = new Date(earliestAvailableDate);
  const isFeeDateSchedulable = !!dateFromFee && !isBefore(new Date(dateFromFee), availableDate);
  const orderBundlingDateHasDeliveryFee =
    !!dateFromFee && !!orderBundlingDate && isSameDay(new Date(dateFromFee), new Date(orderBundlingDate));
  return (
    (fee_type === 'same_day_delivery' || fee_type === 'next_day_delivery') &&
    isFeeDateSchedulable &&
    // don't show this delivery fee option if there is a order bundling option on that date
    !orderBundlingDateHasDeliveryFee
  );
};

export const feeTypeToFormValue: Record<ShipmentFeeType, string> = {
  asap_delivery: 'asap_delivery',
  same_day_delivery: 'same-day-delivery',
  next_day_delivery: 'next-day-delivery',
  standard_delivery: 'any-day-delivery',
  on_demand_delivery: 'on-demand-delivery',
  essentials_only_delivery: 'essentials-only-delivery',
};

export const formValueToFeeType: Record<string, ShipmentFeeType> = {
  'same-day-delivery': 'same_day_delivery',
  'next-day-delivery': 'next_day_delivery',
  'any-day-delivery': 'standard_delivery',
  'on-demand-delivery': 'on_demand_delivery',
  'essentials-only-delivery': 'essentials_only_delivery',
  'asap-delivery': 'asap_delivery',
};

export const getFormattedDate = (date: string | undefined): string =>
  date ? format(parse(date, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0), DATE_FORMATS.WEEKDAY_MONTH_ORDINAL_DATE) : '';

export const getIsToday = (date: string | undefined): boolean =>
  date ? isToday(parse(date, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0)) : false;

export const getIsOrderBundledSelected = (
  radioValue: string,
  preSelect: string,
  hasEarliestUpcomingShipment: boolean,
): boolean =>
  (radioValue === 'same-day-order-bundling' ||
    radioValue === 'order-bundling' ||
    preSelect === 'order-bundling' ||
    preSelect === 'same-day-order-bundling') &&
  hasEarliestUpcomingShipment;

export const getDisabledColor = (loading: boolean) =>
  loading ? COLORS.TEXT_COLORS.DISABLED : COLORS.TEXT_COLORS.PRIMARY;

export const getAnyDayFee = (standardDeliveryFee: number) =>
  standardDeliveryFee ? getFormattedPrice(standardDeliveryFee) : 'Free';

export const getDateLabel = (date: string) => {
  const parsedDate = parse(date, 'yyyy-MM-dd', 0);
  const isDateToday = isToday(parsedDate);
  const isDateTomorrow = isTomorrow(parsedDate);

  if (isDateToday) {
    return `Today, ${format(parsedDate, 'MMM do')}`;
  }
  if (isDateTomorrow) {
    return `Tomorrow, ${format(parsedDate, 'MMM do')}`;
  }
  return format(parsedDate, 'EEE, MMM do');
};

export const getStandardDeliveryDate = ({
  feeToDateHash,
  earliestAvailableDate,
  orderBundlingDate,
}: {
  feeToDateHash: Record<ShipmentFeeType, string | null>;
  earliestAvailableDate: string;
  orderBundlingDate: string | undefined;
}) => {
  // return standard delivery date if it exists
  const standardDeliveryDate = feeToDateHash.standard_delivery;
  if (standardDeliveryDate) {
    return standardDeliveryDate;
  }

  const availableDates = [];
  const sameDayFeeDate = feeToDateHash.same_day_delivery;
  const nextDayFeeDate = feeToDateHash.next_day_delivery;

  if (sameDayFeeDate) {
    availableDates.push(parse(sameDayFeeDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0));
  }

  if (nextDayFeeDate) {
    availableDates.push(parse(nextDayFeeDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0));
  }

  if (orderBundlingDate) {
    availableDates.push(parse(orderBundlingDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0));
  }

  availableDates.sort((a, b) => (isAfter(a, b) ? -1 : 1));

  const earliestAvailableDateString =
    earliestAvailableDate.length > 0 ? earliestAvailableDate : format(new Date(), DATE_FORMATS.YEAR_MONTH_DAY_DASHED);

  // anyDateDeliveryDate will be one day after any delivery fee date or any order bundling date
  // otherwise, it will be the earliest available date
  const anyDateDeliveryDate = availableDates[0]
    ? addDays(availableDates[0], 1)
    : parse(earliestAvailableDateString, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0);

  return format(anyDateDeliveryDate, DATE_FORMATS.YEAR_MONTH_DAY_DASHED);
};

export const computeCheckoutDateItems = ({
  availableShipmentFees,
  dateToFeeHash,
  earliestAvailableDate,
  earliestAvailableDateIsBeforeOrderBundlingDate,
  feeToDateHash,
  orderBundlingDate,
  orderDate,
  radioValue,
  shipmentFeeAvailableHash,
  showEarliestAvailableDate,
}: {
  availableShipmentFees: AvailableShipmentFee[] | undefined | null;
  dateToFeeHash: Record<string, ShipmentFeeType | null>;
  earliestAvailableDate: string;
  earliestAvailableDateIsBeforeOrderBundlingDate: boolean | undefined;
  feeToDateHash: Record<ShipmentFeeType, string | null>;
  orderBundlingDate: string | undefined;
  radioValue: RadioValue;
  orderDate: string | undefined | null;
  showEarliestAvailableDate: boolean;
  shipmentFeeAvailableHash: Record<ShipmentFeeType, AvailableShipmentFee | null>;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const checkoutDateItems = [] as CheckoutDateItem[];
  const isOrderBundlingDateSchedulable =
    orderBundlingDate && !isBefore(new Date(orderBundlingDate), new Date(earliestAvailableDate));
  const isOrderBundlingOptionToday = getIsToday(orderBundlingDate);

  // display order bundling option if applies
  if (isOrderBundlingDateSchedulable) {
    checkoutDateItems.push({
      type: 'order-bundling',
      date: orderBundlingDate,
      label: getDateLabel(orderBundlingDate),
      feeAmountLabel: isOrderBundlingOptionToday ? '+ $0.00' : 'Free',
      feeOriginalAmountLabel: undefined,
      description: 'Add to upcoming delivery for fewer boxes and trips',
      tag: isOrderBundlingOptionToday ? 'expedited' : undefined,
    });
  }

  // display earliest available date option if its date is not on any delivery fee date or order bundling date
  if (showEarliestAvailableDate) {
    checkoutDateItems.push({
      type: 'earliest-available-date',
      date: earliestAvailableDate,
      label: getDateLabel(earliestAvailableDate),
      feeAmountLabel: 'Free',
      feeOriginalAmountLabel: undefined,
      description: 'Earliest available',
      tag: undefined,
    });
  }

  // display same-day and next-day delivery options if its not on order bundling date
  (availableShipmentFees || []).map((fee) => {
    const { fee_type, date, amount, original_amount } = fee;
    const showFeeDeliveryOption = shouldShowFeeDeliveryOption({
      fee_type,
      earliestAvailableDate,
      feeToDateHash,
      orderBundlingDate,
    });

    if (showFeeDeliveryOption) {
      const feeItem: CheckoutDateItem = {
        type: feeTypeToFormValue[fee_type] as RadioValue,
        date,
        label: getDateLabel(date),
        feeAmountLabel: amount === 0 ? 'Free' : getFormattedPrice(amount),
        feeOriginalAmountLabel: getFormattedPrice(original_amount),
        tag: fee_type === 'same_day_delivery' ? 'expedited' : undefined,
      };
      checkoutDateItems.push(feeItem);
    }
  });

  // display any-day delivery option for standard delivery fee dates or free status quo delivery dates
  const availableStandardFee = shipmentFeeAvailableHash?.standard_delivery;
  const { amount, original_amount } = availableStandardFee || {};
  const selectDateTitle = getSelectDateTitle({
    dateToFeeHash,
    orderDate,
    radioValue,
    earliestAvailableRadioVisible: earliestAvailableDateIsBeforeOrderBundlingDate,
    earliestAvailableDate,
  });
  // for standard delivery fee, convert the fee amount into price or Free
  // for status quo free delivery, feeAmountLabel is Free and feeOriginalAmountLabel should be undefined
  const feeAmountLabel = amount !== undefined ? getAnyDayFee(amount) : 'Free';
  const feeOriginalAmountLabel = original_amount !== undefined ? getAnyDayFee(original_amount) : undefined;
  checkoutDateItems.push({
    type: 'any-day-delivery',
    date: getStandardDeliveryDate({ feeToDateHash, earliestAvailableDate, orderBundlingDate }),
    label: selectDateTitle,
    feeAmountLabel,
    feeOriginalAmountLabel,
    tag: undefined,
  });

  // sort the checkout date items by date
  checkoutDateItems.sort((a, b) =>
    isBefore(parse(a.date, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0), parse(b.date, DATE_FORMATS.YEAR_MONTH_DAY_DASHED, 0))
      ? -1
      : 1,
  );

  return checkoutDateItems;
};
